import { Typography, Box, Tooltip } from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { viewOriginalDocument } from '../../utils/documents/documentHelpers';
import { isSafeUrl, isExternalUrl, extractDocumentId } from '../../utils/security/linkSafety';
import { log } from '../../utils/errors/logger';
import { ChatSource, SourceLocator } from '../../api/chatTypes';
import { pickLocatorForStatement } from './passageBinding';
import { useCitationContext } from './CitationContext';
import { formatSourceLocator } from './sourceLocator';
import { documentTypeIcon, documentTypeColor, resolveTypeHint } from './documentTypeIcon';
import { CodeBlock } from './CodeBlock';
import { createMarkdownImageOverride } from '../common/markdownImageSuppression';

/** Sentinel the backend may emit; strip it defensively before rendering. Exported for unit testing only. */
export const KEINE_QUELLEN_RE = /\[\[KEINE QUELLEN\]\]/g;

/**
 * Lift list-nested fenced code blocks to column 0 so they render as proper
 * highlighted code with correct indentation.
 *
 * The LLM often emits a code block INSIDE a list item, indenting the fence and
 * every code line by the list-continuation indent (commonly 3 spaces):
 *
 *   2. **Variante B:**
 *      ```abap
 *      CASE x.
 *        WHEN 1.
 *      ```
 *
 * That indentation is fragile under markdown parsing (the closing fence is
 * indented too) and can break the block or mangle indentation. We detect each
 * fenced block (opening ``` to closing ```), compute the fence's own leading
 * indent, and strip exactly that many leading spaces from the fence lines AND
 * every line in between. The code's RELATIVE indentation (e.g. WHEN nested
 * under CASE) is preserved because the same prefix is removed from all lines.
 * Code OUTSIDE fences is untouched. Exported for unit testing only.
 */
export function dedentFencedCodeBlocks(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  let i = 0;
  const fenceRe = /^(\s*)(`{3,}|~{3,})(.*)$/;
  while (i < lines.length) {
    const m = fenceRe.exec(lines[i]);
    if (!m) {
      out.push(lines[i]);
      i++;
      continue;
    }
    const indent = m[1].length;
    const marker = m[2];
    // Find the matching closing fence (same marker char, >=3 of them).
    const closeRe = new RegExp(`^\\s*${marker[0]}{${marker.length},}\\s*$`);
    const block: string[] = [lines[i]];
    let j = i + 1;
    let closed = false;
    while (j < lines.length) {
      block.push(lines[j]);
      if (closeRe.test(lines[j])) {
        closed = true;
        break;
      }
      j++;
    }
    if (indent > 0) {
      const prefix = ' '.repeat(indent);
      for (let k = 0; k < block.length; k++) {
        block[k] = block[k].startsWith(prefix) ? block[k].slice(indent) : block[k].replace(/^\s+/, '');
      }
    }
    out.push(...block);
    i = closed ? j + 1 : j;
  }
  return out.join('\n');
}

/**
 * Apply a text transform to everything EXCEPT fenced code blocks, so
 * citation-cleanup whitespace collapsing never mangles code indentation.
 * Splits the text into alternating non-code / code segments on ``` fences and
 * transforms only the non-code parts. Exported for unit testing only.
 */
export function applyOutsideCodeFences(text: string, fn: (s: string) => string): string {
  const parts = text.split(/(```[\s\S]*?```|~~~[\s\S]*?~~~)/g);
  return parts
    .map((part, idx) => (idx % 2 === 1 ? part : fn(part)))
    .join('');
}

/**
 * Defensive title cleanup: if a title looks like "<hexid>.bin" (old stored
 * filename), strip the extension so it reads as "<hexid>" instead of a raw
 * binary filename. Real human titles from the new backend are unaffected.
 */
function cleanTitle(title: string | null | undefined): string {
  if (!title) {
    return '';
  }
  // Match only if the entire title is a hex-like string followed by .bin
  const binMatch = title.match(/^([0-9a-f]{8,})\.(bin)$/i);
  if (binMatch) {
    return binMatch[1];
  }
  return title;
}

/**
 * CITATION RUN REGEX — matches a [[...]] block that contains citation ids,
 * possibly comma-separated and with stray brackets (model-generated grouped
 * form). Examples it matches:
 *   [[1]]         [[1.2]]       [[1], [3]]    [[1, 3]]     [[1.1, 2.1]]
 *
 * Strategy: match any [[...]] run where the interior is composed only of
 * digits, dots, commas, spaces, and brackets — then extract all \d+(\.\d+)?
 * tokens from the captured interior.
 */
const CITATION_RUN_RE = /\[\[([\d.,\s[\]]+?)\]\]/g;

/** Extract all citation id strings (\d+(\.\d+)?) from a run interior. */
const CITATION_ID_INNER_RE = /\d+(?:\.\d+)?/g;

/**
 * Split a text string into alternating plain-text and citation-id segments.
 * Returns an array of:
 *   { type: 'text', value: string }
 *   { type: 'citation', id: string }
 *
 * A single grouped run like "[[1], [3]]" is exploded into multiple citation
 * segments (one per resolved id) separated by a 'text' separator ", ".
 *
 * Exported for unit testing only.
 */
type TextSegment = { type: 'text'; value: string };
type CitationSegment = { type: 'citation'; id: string };
export type ContentSegment = TextSegment | CitationSegment;

/** Exported for unit testing only. */
export function splitCitationMarkers(text: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  // Reset lastIndex before the exec loop (regex is module-scoped with /g flag).
  CITATION_RUN_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = CITATION_RUN_RE.exec(text)) !== null) {
    // Append any text before this match.
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }

    // Extract all numeric ids from the run interior.
    const interior = match[1];
    CITATION_ID_INNER_RE.lastIndex = 0;
    const ids: string[] = [];
    let idMatch: RegExpExecArray | null;
    while ((idMatch = CITATION_ID_INNER_RE.exec(interior)) !== null) {
      ids.push(idMatch[0]);
    }

    // Emit one citation segment per id, space-separated (no commas — the user
    // wants citations grouped without commas, e.g. "([[1.2]] [[2.1]])").
    for (let i = 0; i < ids.length; i++) {
      if (i > 0) {
        segments.push({ type: 'text', value: ' ' });
      }
      segments.push({ type: 'citation', id: ids[i] });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return segments;
}

/**
 * CITATION_BEFORE_PUNCT_RE — matches a run of one or more [[...]] markers
 * (including space-separated adjacent ones) that sits immediately before a
 * sentence-ending punctuation mark ("." "!" "?"), optionally preceded by
 * whitespace.  Group 1 captures the leading whitespace; group 2 captures the
 * full marker run (may contain spaces between adjacent markers); group 3
 * captures the punctuation character.
 *
 * Examples it matches:
 *   "...wird [[1.1]] [[1.2]]."   -> G1='', G2='[[1.1]] [[1.2]]', G3='.'
 *   "Satz [[1]] !"               -> G1=' ', G2='[[1]]',          G3='!'
 *
 * NON-match (marker already after punct):
 *   "...wird. ([[1.1]] [[1.2]])" -> not matched
 */
const CITATION_BEFORE_PUNCT_RE =
  /([ \t]*)((?:\[\[[\d.,\s[\]]+?\]\][ \t]*)+)([.!?;:,])/g;

/**
 * Normalize citation marker placement: move any marker run that appears
 * BEFORE a sentence-ending punctuation mark to AFTER the punctuation, wrapped
 * in parentheses.
 *
 *   "...wird [[1.1]] [[1.2]]."  ->  "...wird. ([[1.1]] [[1.2]])"
 *   "Satz [[1]]!"               ->  "Satz! ([[1]])"
 *
 * Also handles end-of-paragraph/string: a run at the very end with no trailing
 * punctuation gets a period injected:
 *
 *   "Satz am Ende [[1]]"        ->  "Satz am Ende. ([[1]])"
 *
 * Idempotent: if markers are already in parentheses after the punctuation the
 * input won't match the pattern (no punct immediately after the run) so it is
 * left unchanged.
 *
 * Exported for unit testing only.
 */
export function normalizeCitationPlacement(text: string): string {
  // Canonical placement (Perplexity/NotebookLM convention, mirrored by the
  // server-side normaliser): the marker run sits directly after the closing
  // punctuation of its statement, no parentheses, no space before it.
  // Runs only OUTSIDE code fences so code is never touched.
  return applyOutsideCodeFences(text, (s) => {
    // Legacy persisted form "Text. ([[1]] [[2]])" and model-emitted parens.
    let out = s.replace(
      /\(\s*((?:\[\[[\d.,\s[\]]+?\]\]\s*)+)\)/g,
      (_full, run: string) => run.replace(/\s+/g, '')
    );
    // Marker run before closing punctuation moves behind it.
    CITATION_BEFORE_PUNCT_RE.lastIndex = 0;
    out = out.replace(
      CITATION_BEFORE_PUNCT_RE,
      (_full, _leadingWs: string, markerRun: string, punct: string) =>
        `${punct}${markerRun.replace(/\s+/g, '')}`
    );
    // No whitespace between text and a marker; no whitespace inside a run.
    out = out.replace(/[ \t]+(\[\[[\d.,\s[\]]+?\]\])/g, '$1');
    out = out.replace(/(\]\])[ \t]+(\[\[)/g, '$1$2');
    // Consecutive repeats of the same marker collapse.
    out = out.replace(/(\[\[(\d+(?:\.\d+)?)\]\])(?:\1)+/g, '$1');
    // Leftover doubled spaces from removed markers.
    return out.replace(/[ \t]{2,}/g, ' ');
  });
}

/**
 * Remove citation markers that don't resolve to a real source, then clean up any
 * empty citation parentheses left behind. The backend now groups citations in
 * parentheses at the end of a paragraph (e.g. "Text. ([[1.2]] [[2.1]])"); if a
 * cited id has no matching source (orphan/hallucinated), it must vanish from the
 * prose — and if a whole group is stripped, the empty "()" must go too.
 *
 * Exported for unit testing only.
 */
export function stripOrphanCitations(text: string, validIds: Set<string>): string {
  // 1. Drop any [[id]] / [[id, id]] run whose ids are ALL unresolved; within a
  //    mixed run keep only the resolved ids.
  let out = text.replace(CITATION_RUN_RE, (_full, interior: string) => {
    const kept = (interior.match(CITATION_ID_INNER_RE) ?? []).filter((id) => validIds.has(id));
    if (kept.length === 0) {
      return ''; // all orphan -> remove the whole marker
    }
    return `[[${kept.join(' ')}]]`; // keep resolved ids, space-separated
  });

  // 2. Tidy ONLY parentheses that contain citation markers (leave normal prose
  //    parentheses untouched). Trim whitespace just inside such parens
  //    ("( [[1]] )" -> "([[1]])") and remove a citation paren that became empty
  //    after stripping ("Text. ()" -> "Text.").
  out = out.replace(/(\s*)\(([^()]*)\)/g, (full, lead: string, inner: string) => {
    if (!inner.includes('[[') && inner.trim() !== '') {
      return full; // not a citation paren and not empty — leave as-is
    }
    const trimmed = inner.replace(/\s+/g, ' ').trim();
    // Empty after stripping: drop the parens AND the space that preceded them.
    return trimmed === '' ? '' : `${lead}(${trimmed})`;
  });
  // Collapse a double space left where an inline marker was removed -- but only
  // OUTSIDE code fences, so code indentation is preserved.
  out = applyOutsideCodeFences(out, (s) => s.replace(/[ \t]{2,}/g, ' '));

  return out;
}

export type CitationChipVariant = 'compact' | 'inline-tag' | 'superscript';

/**
 * Build a human-readable location line for the citation card, e.g.
 * "Seite 2 · 1.1 Ziel und Geltungsbereich". Returns '' when the source has no
 * usable locator. Uses the i18n kind word so it follows the UI language.
 */
function buildRichLocatorLine(
  source: ChatSource,
  t: TFunction,
  boundLocator?: SourceLocator | null
): string {
  const locator = boundLocator ?? source.locator ?? source.locators?.[0] ?? null;
  if (!locator) {
    return '';
  }
  const parts = formatSourceLocator(locator);
  if (!parts) {
    return '';
  }
  const pieces: string[] = [];
  if (parts.numeric) {
    const label = t(
      `chat.sources.locator.kind.${parts.numeric.kindKey}.${
        parts.numeric.count > 1 ? 'other' : 'one'
      }`,
      { positions: parts.numeric.positions, defaultValue: `${parts.numeric.positions}` }
    );
    pieces.push(label);
  }
  if (parts.section) {
    pieces.push(parts.section);
  }
  return pieces.join(' · ');
}

interface CitationCardContentProps {
  source: ChatSource;
  id: string;
  locatorLine: string;
  /** The passage this chip is bound to (statement-matched); overrides the
   *  default first-passage quote in the hover card. */
  boundLocator?: SourceLocator | null;
}

/**
 * Rich hover-card body for a citation: type icon + document title, the exact
 * page/section location, and the cited excerpt quote. Each part is omitted when
 * its data is absent.
 *
 * No confidence indicator is shown: the rerank score is not a calibrated
 * probability of correctness, and research on answer-engine UX found that an
 * uncalibrated confidence signal harms appropriate user trust more than it
 * helps. Source attribution + the cited excerpt are the trust signals instead.
 */
const CitationCardContent: React.FC<CitationCardContentProps> = ({
  source,
  id,
  locatorLine,
  boundLocator,
}) => {
  const { t } = useTranslation();
  const displayTitle = cleanTitle(source.title);
  const typeHint = resolveTypeHint(source.source_type, source.file_type, source.url);
  const accent = documentTypeColor(typeHint, source.title);
  // Quote preview: the statement-bound passage's quote when the chip is bound
  // (NotebookLM model); otherwise per-chunk source.excerpt, then the best
  // chunk's excerpt from the locator.
  const quote =
    boundLocator?.excerpt ??
    source.excerpt ??
    source.locator?.excerpt ??
    source.locators?.[0]?.excerpt ??
    null;

  return (
    <Box sx={{ maxWidth: 320, p: 0.5 }}>
      {/* Title row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: locatorLine || quote ? 0.75 : 0 }}>
        {documentTypeIcon(typeHint, source.title, 18)}
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: '0.78rem',
            color: '#1a2a3a',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {t('chat.sources.chip.sourceLabel', { defaultValue: 'Quelle {{n}}', n: id })} · {displayTitle}
        </Typography>
      </Box>

      {/* Location line */}
      {locatorLine && (
        <Typography
          component="div"
          sx={{ fontSize: '0.72rem', color: '#3a4a5f', lineHeight: 1.35, mb: quote ? 0.75 : 0 }}
        >
          {locatorLine}
        </Typography>
      )}

      {/* Excerpt quote */}
      {quote && (
        <Typography
          component="div"
          sx={{
            fontSize: '0.72rem',
            fontStyle: 'italic',
            color: '#46566a',
            lineHeight: 1.4,
            pl: 0.75,
            borderLeft: `2px solid ${accent}`,
          }}
        >
          {quote}
        </Typography>
      )}
    </Box>
  );
};

interface CitationChipProps {
  /** The citation id string from the marker, e.g. "1" or "1.2". */
  id: string;
  sources: ChatSource[];
  variant?: CitationChipVariant;
  /** Answer text preceding this marker — binds the chip to the passage that
   *  supports THIS statement (hover quote + click jump), not just the doc. */
  statement?: string;
}

/**
 * Inline citation chip. Resolves [[id]] to the ChatSource where
 * citation_id === id (string comparison).
 *
 * Bug A: if the id does NOT resolve to any source, renders NOTHING (no greyed
 * chip, no placeholder). The caller must check resolution before rendering.
 *
 * Clicking scrolls to source-card-{id} and flashes it.
 * Hovering sets activeCitation = id in CitationContext so the matching source
 * row highlights. Keyboard: Enter/Space activates.
 */
export const CitationChip: React.FC<CitationChipProps> = ({ id, sources, statement }) => {
  const { t } = useTranslation();
  const { setActiveCitation, clearActiveCitation, activeCitation, revealCitation, openDocument } =
    useCitationContext();

  // Resolve by citation_id (string comparison; coerce since older messages
  // persist citation_id as a number).
  const source = sources.find(
    (s) => s.citation_id !== null && s.citation_id !== undefined && String(s.citation_id) === id
  );

  // Bug A: if unresolvable, render nothing at all.
  if (!source) {
    return null;
  }

  const isActive = activeCitation === id;

  const handleActivate = () => {
    // Sources renders collapsed by default; the reveal request expands it
    // first, then scrolls to and flashes the citation's card (handled there) —
    // useful context once the viewer closes again.
    revealCitation(id);
    // NotebookLM binding model: chip click jumps STRAIGHT into the in-app
    // viewer at the passage supporting THIS statement; the sources list stays
    // the secondary path for picking other passages.
    if (openDocument && source.document_id) {
      openDocument(source.document_id, statement);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
  };

  const boundLocator = pickLocatorForStatement(source, statement);
  const locatorLine = buildRichLocatorLine(source, t, boundLocator);

  return (
    <Tooltip
      title={
        <CitationCardContent
          source={source}
          id={id}
          locatorLine={locatorLine}
          boundLocator={boundLocator}
        />
      }
      placement="top"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#ffffff',
            color: '#1a2a3a',
            border: '1px solid #dde3ea',
            borderRadius: '8px',
            boxShadow: '0 6px 24px rgba(0,0,0,0.14)',
            maxWidth: 'none',
            p: 1,
          },
        },
        arrow: { sx: { color: '#ffffff', '&::before': { border: '1px solid #dde3ea' } } },
      }}
    >
      <Box
        component="sup"
        onClick={handleActivate}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setActiveCitation(id)}
        onMouseLeave={clearActiveCitation}
        onFocus={() => setActiveCitation(id)}
        onBlur={clearActiveCitation}
        aria-label={`Source ${id}: ${cleanTitle(source.title)}`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '18px',
          height: '18px',
          px: '4px',
          borderRadius: '9px',
          fontSize: '0.65rem',
          fontWeight: 600,
          lineHeight: 1,
          verticalAlign: 'super',
          mx: '1px',
          cursor: 'pointer',
          backgroundColor: isActive ? '#004a99' : '#003064',
          color: '#ffffff',
          userSelect: 'none',
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: '#004a99' },
          '&:focus-visible': { outline: '2px solid #003064', outlineOffset: '1px' },
        }}
      >
        {id}
      </Box>
    </Tooltip>
  );
};

/**
 * Walk React children and replace [[id]] text markers with CitationChip elements.
 * Unresolvable ids produce no chip (Bug A). Grouped forms like "[[1], [3]]" each
 * yield their own chip (Bug B). Returns an array of React nodes safe to spread
 * inside a JSX element.
 */
function renderWithCitationChips(
  children: React.ReactNode,
  sources: ChatSource[],
  variant: CitationChipVariant
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      const segments = splitCitationMarkers(child);
      if (segments.length === 1 && segments[0].type === 'text') {
        return child; // fast path — no markers
      }
      // Track the text preceding each marker: it is the STATEMENT the chip
      // cites, used to bind the chip to the supporting passage.
      let preceding = '';
      return segments.map((seg, i) => {
        if (seg.type === 'citation') {
          return (
            <CitationChip
              key={i}
              id={seg.id}
              sources={sources}
              variant={variant}
              statement={preceding || undefined}
            />
          );
        }
        preceding = `${preceding}${seg.value}`.slice(-300);
        return seg.value;
      });
    }
    // Recurse into nested elements (e.g. a [[N]] inside **bold** or a link) so
    // markers convert no matter how deep react-markdown nested the text.
    if (React.isValidElement(child)) {
      const el = child as React.ReactElement<{ children?: React.ReactNode }>;
      if (el.props.children !== undefined && el.props.children !== null) {
        return React.cloneElement(el, {
          children: renderWithCitationChips(el.props.children, sources, variant),
        });
      }
    }
    return child;
  });
}

interface MarkdownMessageProps {
  content: string;
  isUser: boolean;
  /** ChatSource array — used to render [[N]] inline citation chips. */
  sources?: ChatSource[];
  /**
   * Visual style for inline citation chips.
   * 'compact' (default): icon+number modern soft pill with popover on click.
   * 'inline-tag' (Variant A): text-adjacent tag with bracket notation.
   * 'superscript' (Variant C): minimal numeric superscript.
   */
  chipVariant?: CitationChipVariant;
  /**
   * Opens a document by id when an in-text `document:` citation link is clicked.
   * Routes text/manual sources to the in-app preview drawer (and others to the
   * original-document viewer). When omitted, falls back to viewOriginalDocument.
   */
  onOpenDocument?: (documentId: string) => void;
}

export const MarkdownMessage: React.FC<MarkdownMessageProps> = ({
  content,
  isUser,
  sources,
  chipVariant = 'compact',
  onOpenDocument,
}) => {
  const { t } = useTranslation();

  const resolvedSources: ChatSource[] = sources ?? [];

  // Stable signature of the valid citation-id set, so the content pipeline memo
  // below only recomputes when the ids actually change (not on every render).
  const validIdsKey = resolvedSources
    .map((s) => (s.citation_id !== null && s.citation_id !== undefined ? String(s.citation_id) : ''))
    .join(',');

  // Defensive strip of backend sentinel + orphan citation removal + placement
  // normalization. Memoized on (content, ids): the text pipeline (dedent fences,
  // regex passes) ran on EVERY render before, which was needless work for an
  // unchanged message. dedent runs FIRST so list-nested code renders correctly.
  const safeContent = React.useMemo(() => {
    const validIds = new Set<string>(validIdsKey.split(',').filter(Boolean));
    return normalizeCitationPlacement(
      stripOrphanCitations(
        dedentFencedCodeBlocks(content).replace(KEINE_QUELLEN_RE, ''),
        validIds
      )
    );
  }, [content, validIdsKey]);

  const cite = (children: React.ReactNode) =>
    renderWithCitationChips(children, resolvedSources, chipVariant);

  const markdownComponents = {
    p: ({ children, ...props }: React.ComponentProps<'p'>) => {
      return (
        <p
          style={{
            fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
            color: '#333333',
            margin: '0.5em 0'
          }}
          {...props}
        >
          {cite(children)}
        </p>
      );
    },
    li: ({ children, ...props }: React.ComponentProps<'li'>) => (
      <li {...props}>{cite(children)}</li>
    ),
    h1: ({ children, ...props }: React.ComponentProps<'h1'>) => (
      <h1 {...props}>{cite(children)}</h1>
    ),
    h2: ({ children, ...props }: React.ComponentProps<'h2'>) => (
      <h2 {...props}>{cite(children)}</h2>
    ),
    h3: ({ children, ...props }: React.ComponentProps<'h3'>) => (
      <h3 {...props}>{cite(children)}</h3>
    ),
    h4: ({ children, ...props }: React.ComponentProps<'h4'>) => (
      <h4 {...props}>{cite(children)}</h4>
    ),
    h5: ({ children, ...props }: React.ComponentProps<'h5'>) => (
      <h5 {...props}>{cite(children)}</h5>
    ),
    h6: ({ children, ...props }: React.ComponentProps<'h6'>) => (
      <h6 {...props}>{cite(children)}</h6>
    ),
    strong: ({ children, ...props }: React.ComponentProps<'strong'>) => (
      <strong {...props}>{cite(children)}</strong>
    ),
    em: ({ children, ...props }: React.ComponentProps<'em'>) => (
      <em {...props}>{cite(children)}</em>
    ),
    td: ({ children, ...props }: React.ComponentProps<'td'>) => (
      <td {...props}>{cite(children)}</td>
    ),
    blockquote: ({ children, ...props }: React.ComponentProps<'blockquote'>) => (
      <blockquote {...props}>{cite(children)}</blockquote>
    ),
    // Syntax-highlighted code. Fenced blocks (```lang) carry a `language-xxx`
    // class and render via CodeBlock (highlight + copy); inline `code` renders
    // as a styled span. Citation chips are NOT injected into code (a [[N]] in
    // code is literal text, not a citation).
    code: ({ className, children, ...props }: React.ComponentProps<'code'> & { inline?: boolean }) => {
      const isInline = (props as { inline?: boolean }).inline ?? !/language-/.test(className ?? '');
      return (
        <CodeBlock className={className} inline={isInline}>
          {children}
        </CodeBlock>
      );
    },
    // react-markdown wraps fenced code in <pre><code>. CodeBlock already renders
    // its own container, so pass <pre> through transparently (no default styling).
    pre: ({ children }: React.ComponentProps<'pre'>) => <>{children}</>,
    // Images are never rendered as <img> (no src ever reaches the DOM); only
    // the alt text, if any, is shown as inert placeholder text.
    img: createMarkdownImageOverride(t),
    a: ({href, children, ...props}: React.ComponentProps<'a'>) => {
      // SECURITY: Validate URL safety to prevent XSS attacks
      // Blocks: javascript:, data:, vbscript:, file: protocols
      // OWASP: A03:2021 - Injection
      if (!isSafeUrl(href)) {
        log.warn('[MarkdownMessage] Rendering unsafe link as text', {
          href: href ? href.substring(0, 50) : 'undefined'
        });
        return <span style={{ color: '#666' }}>{children}</span>;
      }

      // Handle document links (format: document:documentId)
      if (href?.startsWith('document:')) {
        const documentId = extractDocumentId(href);

        if (!documentId) {
          log.warn('[MarkdownMessage] Invalid document ID format', {
            href: href.substring(0, 50)
          });
          return <span style={{ color: '#666' }}>{children}</span>;
        }

        return (
          <a
            style={{ color: '#003064', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={(e) => {
              e.preventDefault();
              if (onOpenDocument) {
                onOpenDocument(documentId);
              } else {
                void viewOriginalDocument(documentId);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={t('chat.sources.actions.viewDocument')}
          >
            {children || t('chat.sources.actions.viewDocument')}
          </a>
        );
      }

      // SECURITY: External links - add security attributes
      if (isExternalUrl(href || '')) {
        return (
          <a
            style={{ color: '#003064', textDecoration: 'underline' }}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
            aria-label={typeof children === 'string' ? children : 'External link'}
          >
            {children || href}
          </a>
        );
      }

      // Internal links (relative URLs)
      return (
        <a
          style={{ color: '#003064', textDecoration: 'underline' }}
          href={href}
          {...props}
          aria-label={typeof children === 'string' ? children : 'Link'}
        >
          {children || href}
        </a>
      );
    }
  };

  if (isUser) {
    return (
      <Typography
        variant="body1"
        sx={{
          fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
          color: '#333333'
        }}
      >
        {safeContent}
      </Typography>
    );
  }

  return (
    <ReactMarkdown components={markdownComponents}>
      {safeContent}
    </ReactMarkdown>
  );
};
